import { UserRole, TenantType } from '../types/user.types';

// ============================================================
// Active Roles - These are the 10 roles currently in use
// ============================================================
export const ACTIVE_ROLES: UserRole[] = [
  UserRole.PROPCLOUDAI_SUPER_USER,
  UserRole.HQ_MANAGER,
  UserRole.HQ_ADMIN,
  UserRole.HQ_BONDS,
  UserRole.HQ_CONSULTANT_LVL1,
  UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
  UserRole.HQ_REFERRAL_MANDATE,
  UserRole.HQ_ADVERTISING,
  UserRole.HQ_VIEWING_AGENT,
  UserRole.HQ_VIEWING_COORDINATOR,
  UserRole.HQ_CALL_CENTRE_AGENT,
  UserRole.AI_AGENT_LVL1,
];

// Deprecated roles - kept for migration compatibility
export const DEPRECATED_ROLES: UserRole[] = [
  UserRole.PROPCLOUDAI_USER_MANAGEMENT,
  UserRole.PROPCLOUDAI_CRM,
  UserRole.PROPCLOUDAI_ADVERTISING, // Use HQ_ADVERTISING instead
  UserRole.VIEWING_AGENT, // Use HQ_VIEWING_AGENT instead
  UserRole.INDEPENDENT_PARTNER_LVL3,
  UserRole.PARTNER_AGENT_LVL3,
];

// Role migration map - old role to new role
export const ROLE_MIGRATION_MAP: Partial<Record<UserRole, UserRole>> = {
  [UserRole.PROPCLOUDAI_ADVERTISING]: UserRole.HQ_ADVERTISING,
  [UserRole.VIEWING_AGENT]: UserRole.HQ_VIEWING_AGENT,
  [UserRole.PROPCLOUDAI_USER_MANAGEMENT]: UserRole.HQ_MANAGER,
  [UserRole.PROPCLOUDAI_CRM]: UserRole.HQ_ADMIN,
  [UserRole.INDEPENDENT_PARTNER_LVL3]: UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
  [UserRole.PARTNER_AGENT_LVL3]: UserRole.HQ_CONSULTANT_LVL1,
};

// Check if a role is deprecated
export const isDeprecatedRole = (role: UserRole): boolean => {
  return DEPRECATED_ROLES.includes(role);
};

// Get the migrated role for a deprecated role
export const getMigratedRole = (role: UserRole): UserRole => {
  return ROLE_MIGRATION_MAP[role] || role;
};

// Default commission percentages by role
export const ROLE_COMMISSION_PERCENTAGES: Record<string, number> = {
  // Active commission-earning roles
  [UserRole.HQ_CONSULTANT_LVL1]: 30,
  [UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]: 50,
  [UserRole.HQ_REFERRAL_MANDATE]: 5,
  // System/Management roles - no commission
  [UserRole.PROPCLOUDAI_SUPER_USER]: 0,
  [UserRole.HQ_MANAGER]: 0,
  [UserRole.HQ_ADMIN]: 0,
  [UserRole.HQ_BONDS]: 0,
  [UserRole.HQ_ADVERTISING]: 0,
  [UserRole.HQ_VIEWING_AGENT]: 0,
  [UserRole.HQ_VIEWING_COORDINATOR]: 0,
  [UserRole.HQ_CALL_CENTRE_AGENT]: 0,
  [UserRole.AI_AGENT_LVL1]: 0, // AI agents never earn commission
  // Deprecated roles (kept for backward compatibility)
  [UserRole.PROPCLOUDAI_USER_MANAGEMENT]: 0,
  [UserRole.PROPCLOUDAI_CRM]: 0,
  [UserRole.PROPCLOUDAI_ADVERTISING]: 0,
  [UserRole.VIEWING_AGENT]: 0,
  [UserRole.INDEPENDENT_PARTNER_LVL3]: 50, // Maps to HQ_INDEPENDENT_CONSULTANT_LVL2
  [UserRole.PARTNER_AGENT_LVL3]: 30, // Maps to HQ_CONSULTANT_LVL1
};

// Get the default commission percentage for a role
export const getDefaultCommissionPercentage = (role: UserRole): number => {
  return ROLE_COMMISSION_PERCENTAGES[role] || 0;
};

// Check if a role is a PropCloudAI system role (super admin only now)
export const isSystemRole = (role: UserRole): boolean => {
  return role === UserRole.PROPCLOUDAI_SUPER_USER;
};

// Check if a role is an HQ role (all active roles except super user are HQ roles now)
export const isHQRole = (role: UserRole): boolean => {
  // All hq_ prefixed roles are HQ roles
  // Note: INDEPENDENT_PARTNER_LVL3 and PARTNER_AGENT_LVL3 now map to hq_consultant_lvl3
  return (role as string).startsWith('hq_');
};

// Check if a role is a partner office role (deprecated - partner system discontinued)
export const isPartnerRole = (_role: UserRole): boolean => {
  // Partner roles are fully deprecated - INDEPENDENT_PARTNER_LVL3 and PARTNER_AGENT_LVL3
  // now map to hq_consultant_lvl3, so they are effectively HQ roles
  return false;
};

// Get tenant type based on role (simplified - all roles are HQ now except super user)
export const getTenantTypeByRole = (role: UserRole): TenantType => {
  if (role === UserRole.PROPCLOUDAI_SUPER_USER) {
    return TenantType.PROPCLOUDAI;
  }
  return TenantType.PROPINVEST_HQ;
};

// Check if a role can manage users
export const canManageUsers = (role: UserRole): boolean => {
  return [
    UserRole.PROPCLOUDAI_SUPER_USER,
    UserRole.HQ_MANAGER,
    // Deprecated roles with user management capability
    UserRole.PROPCLOUDAI_USER_MANAGEMENT,
  ].includes(role);
};

// Check if a role can view all data (cross-tenant)
export const canViewAllData = (role: UserRole): boolean => {
  return role === UserRole.PROPCLOUDAI_SUPER_USER;
};

// Get display name for tenant
export const getTenantDisplayName = (tenantType: TenantType, tenantName?: string): string => {
  switch (tenantType) {
    case TenantType.PROPCLOUDAI:
      return 'PropCloudAI System';
    case TenantType.PROPINVEST_HQ:
      return 'PropInvest HQ';
    case TenantType.PARTNER_OFFICE:
      return tenantName || 'Partner Office';
    default:
      return 'Unknown';
  }
};

// Get role category for display
export const getRoleCategory = (role: UserRole): string => {
  if (role === UserRole.PROPCLOUDAI_SUPER_USER) {
    return 'PropCloudAI System';
  } else if (isDeprecatedRole(role)) {
    return 'Deprecated Roles';
  } else {
    return 'PropInvest HQ';
  }
};

// Format role display name with commission
export const formatRoleWithCommission = (role: UserRole, customPercentage?: number): string => {
  const commission = customPercentage || getDefaultCommissionPercentage(role);
  if (commission > 0) {
    return `${getRoleDisplayName(role)} (${commission}%)`;
  }
  return getRoleDisplayName(role);
};

// Get clean display name for role
export const getRoleDisplayName = (role: UserRole): string => {
  const roleNames: Record<string, string> = {
    // Active roles
    [UserRole.PROPCLOUDAI_SUPER_USER]: 'Super Admin',
    [UserRole.HQ_MANAGER]: 'HQ Manager',
    [UserRole.HQ_ADMIN]: 'HQ Admin',
    [UserRole.HQ_BONDS]: 'HQ Bonds',
    [UserRole.HQ_CONSULTANT_LVL1]: 'HQ Consultant (Level 1)',
    [UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]: 'HQ Independent Consultant (Level 2)',
    [UserRole.HQ_REFERRAL_MANDATE]: 'HQ Referral Mandate',
    [UserRole.HQ_ADVERTISING]: 'HQ Advertising',
    [UserRole.HQ_VIEWING_AGENT]: 'HQ Viewing Agent',
    [UserRole.HQ_VIEWING_COORDINATOR]: 'HQ Viewing Coordinator',
    [UserRole.HQ_CALL_CENTRE_AGENT]: 'HQ Call Centre Agent',
    [UserRole.AI_AGENT_LVL1]: 'AI Agent (Level 1)',
    // Deprecated roles (kept for migration display)
    [UserRole.PROPCLOUDAI_USER_MANAGEMENT]: 'User Management (Deprecated)',
    [UserRole.PROPCLOUDAI_CRM]: 'CRM (Deprecated)',
    [UserRole.PROPCLOUDAI_ADVERTISING]: 'Advertising (Deprecated)',
    [UserRole.VIEWING_AGENT]: 'Viewing Agent (Deprecated)',
    [UserRole.INDEPENDENT_PARTNER_LVL3]: 'Independent Partner (Deprecated)',
    [UserRole.PARTNER_AGENT_LVL3]: 'Partner Agent (Deprecated)',
  };
  return roleNames[role] || role;
};

// Get short display name for role (for compact UI)
export const getRoleShortName = (role: UserRole): string => {
  const shortNames: Record<string, string> = {
    [UserRole.PROPCLOUDAI_SUPER_USER]: 'Super Admin',
    [UserRole.HQ_MANAGER]: 'Manager',
    [UserRole.HQ_ADMIN]: 'Admin',
    [UserRole.HQ_BONDS]: 'Bonds',
    [UserRole.HQ_CONSULTANT_LVL1]: 'Consultant L1',
    [UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]: 'Consultant L2',
    [UserRole.HQ_REFERRAL_MANDATE]: 'Referral',
    [UserRole.HQ_ADVERTISING]: 'Advertising',
    [UserRole.HQ_VIEWING_AGENT]: 'Viewing Agent',
    [UserRole.HQ_VIEWING_COORDINATOR]: 'Viewing Coord.',
    [UserRole.HQ_CALL_CENTRE_AGENT]: 'CC Agent',
    [UserRole.AI_AGENT_LVL1]: 'AI Agent',
  };
  return shortNames[role] || getRoleDisplayName(role);
};

// Get role description
export const getRoleDescription = (role: UserRole): string => {
  const descriptions: Record<string, string> = {
    [UserRole.PROPCLOUDAI_SUPER_USER]: 'Full system access with ability to manage all users and settings',
    [UserRole.HQ_MANAGER]: 'HQ operations management with full access to sales and admin features',
    [UserRole.HQ_ADMIN]: 'Administrative tasks and support for HQ operations',
    [UserRole.HQ_BONDS]: 'Bond application management and financial document processing',
    [UserRole.HQ_CONSULTANT_LVL1]: 'Junior consultant with 30% commission on sales',
    [UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]: 'Senior consultant with 50% commission on sales',
    [UserRole.HQ_REFERRAL_MANDATE]: 'Referral mandate earning 5% on referred sales',
    [UserRole.HQ_ADVERTISING]: 'Marketing and advertising team member',
    [UserRole.HQ_VIEWING_AGENT]: 'Property viewing management and client tours',
    [UserRole.HQ_VIEWING_COORDINATOR]: 'Complete oversight and coordination of all viewings',
    [UserRole.HQ_CALL_CENTRE_AGENT]: 'Call centre agent handling calls via Euphoria dialer',
    [UserRole.AI_AGENT_LVL1]:
      'AI sales consultant (server-side actor). Owns and works assigned leads via the ARIA tool layer; no interactive login, no commission, no deal-capture rights.',
  };
  return descriptions[role] || 'Role description not available';
};